import { getFooterData, getHomepageData, getNavigationData } from "@/api";
import { CrumbsItemAtom, HideCrumbsAtom } from "@/atoms";
import { IPageDTOResponse } from "@/interfaces/IPageDTO";
import { NextPage } from "next";
import { useEffect } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { ModuleMatcher2 } from "@/components";

import PagePathAtom from "@/atoms/PagePathAtom";
import Head from "next/head";
import getTargetUrl from "@/api/getTargetUrl";
import Script from "next/script";

export async function getServerSideProps(ctx: any) {
  const { lang } = ctx.params;

  ctx.res.setHeader("Cache-Control", "public, max-age=120");

  const data = await getHomepageData();

  const footerData = await getFooterData();

  const navigationData = await getNavigationData();

  // 404 check
  if (ctx.query["lang"].startsWith("it") || ctx.query["lang"].startsWith("en")) {
    const nextSegment = ctx.query["lang"].slice(3);
    if (nextSegment && nextSegment !== "/") {
      return { notFound: true };
    }
  } else {
    return { notFound: true };
  }


  const dataPassedToModuleMatcher = data.data?.find(
    (item: { xtaxAirports: { ti: any[] } }) =>
      item.xtaxAirports.ti.find(
        (airportItem) =>
          airportItem.label.toLowerCase() ===
          process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase()
      )
  );

  const excludes: string[] = [];

  const dataPassToModuleMatcher = await Promise.all(
    dataPassedToModuleMatcher!.lstModule
      ?.filter(
        (item) => !excludes?.includes(item.xconModules.linkedcontenttype)
      )
      .map(async (moduleItem) => {
        let moduleData: any;

        switch (moduleItem.xconModules.linkedcontenttype) {
          case "m01":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m02":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m03":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m04":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m05":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m06":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            // TODO: fetch delle card?
            break;
          case "m07":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m08":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            // TODO: fetch delle card?
            break;
          case "m09":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m10":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m11":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m12":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m13":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m14":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m15":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            // TODO: check fetch dentro questo modulo
            break;
          case "m16":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            // TODO: fetch delle card?
            break;
          case "m17":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m18":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m19":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          case "m20":
            moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
            break;
          default:
            moduleData = null;
        }

        return {
          linkedcontenttype: moduleItem.xconModules.linkedcontenttype,
          id: moduleItem.id,
          moduleData: moduleData,
        };
      })
  );

  return {
    props: {
      lang,
      data,
      footerData,
      navigationData,
      dataPassToModuleMatcher
    },
  };
}

interface IHomepageProps {
  lang: string;
  data: IPageDTOResponse;
  navigationData: any;
  getTargetUrl: any;
  dataPassToModuleMatcher: any;
}

const Homepage: NextPage<IHomepageProps> = ({
  lang,
  data,
  navigationData,
  dataPassToModuleMatcher,
}) => {
  const pageData = data?.data[0];
  const pagePath = pageData?.xtaxNavigationMenu?.ti?.[0]?.path || "1/2";
  const { i18n } = useTranslation();

  const setPagePath = useSetRecoilState(PagePathAtom);
  const setCrumbsItem = useSetRecoilState(CrumbsItemAtom);
  const setHideCrumbs = useSetRecoilState(HideCrumbsAtom);

  useEffect(() => {
    setPagePath(pagePath);
    setCrumbsItem(navigationData);
    setHideCrumbs(pageData?.flgHideNavigationéath);
  }, [pageData, pagePath, navigationData]);

  useEffect(() => {
    i18n.changeLanguage(lang.toLowerCase());
  }, [lang, i18n]);

  const normalizeUrl = (url: string) => {
    if (!url) return "";
    return url === "/" ? url : url.replace(/\/$/, "");
  };

  const generateStructuredData = (
    pageData: any,
    lang: string,
    siteUrl: string,
    navigationData: any
  ) => {
    const webSiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name:
        process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === "mxp"
          ? "Milano Malpensa Airport"
          : "Milano Linate Airport",
      url: `${siteUrl}/${lang}`,
      potentialAction: {
        "@type": "SearchAction",
        target: `${siteUrl}/${lang}/search?query={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };

    // 2. Organization Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name:
        process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === "mxp"
          ? "Milano Malpensa - Aeroporti di Milano"
          : "Milano Linate - Aeroporti di Milano",
      url: `${siteUrl}/${lang}`,
    };

    // 3. WebPage Schema
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: pageData?.lblSeoTitle[lang],
      description: pageData?.lblSeoDescription[lang],
      url: `${siteUrl}${normalizeUrl(pageData?.lblUrlPath[lang] || "")}`,
    };

    // 4. BreadcrumbList Schema (solo se ci sono breadcrumb)
    let breadcrumbSchema = null;
    if (navigationData && navigationData.length > 0) {
      breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: navigationData.map(
          (item: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@id": `${siteUrl}${item.url}`,
              name: item.name,
            },
          })
        ),
      };
    }

    return {
      webSiteSchema,
      organizationSchema, 
      webPageSchema,
      breadcrumbSchema
    };
  };

  const siteMxp = process.env.NEXT_PUBLIC_SITE_MALPENSA;
  const siteLin = process.env.NEXT_PUBLIC_SITE_LINATE;
  const isMpx = process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE;

  const siteUrl = (isMpx === "mxp" ? siteMxp : siteLin) || "";


  const structuredData = generateStructuredData(
    pageData,
    lang,
    siteUrl,
    navigationData
  );


  const isMxpSite = process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === "mxp";

  const sasInitScript = `
    (function () {
      var w = window;
      var sas = w.sas = w.sas || {};
      sas.cmd = sas.cmd || [];

      // Evita di richiamare più volte sas.setup (fix per Chrome/Firefox + StrictMode)
      if (!w.__sasSetupDone) {
        sas.cmd.push(function () {
          sas.setup({
            networkid: 2661,
            domain: 'https://www14.smartadserver.com',
            async: true
          });
        });
        w.__sasSetupDone = true;
      }

      sas.cmd.push(function () {
        sas.call("onecall", {
          siteId: ${isMxpSite ? 299227 : 299226},
          pageId: ${isMxpSite ? 1095953 : 1095950},
          formats: [
            { id: 56461 },
            { id: 56463 }
          ],
          target: 'lang=${lang}'
        });
      });
    })();
  `;

  return (
    <>
      <Head>
        <title>{pageData?.lblSeoTitle?.[lang] || "Milano Airport"}</title>
        <meta
          name="title"
          content={pageData?.lblSeoTitle?.[lang] || "Milano Airport"}
        ></meta>
        <meta
          name="description"
          content={pageData?.lblSeoDescription?.[lang] || ""}
        ></meta>
        <meta
          name="google-site-verification"
          content="O9KGBBGOrdaWpgZGBNlsq0Y1qBChD-aV62vMbPF8X1M"
        />

        <link
          rel="canonical"
          href={`${siteUrl}${normalizeUrl(
            pageData?.lblUrlPath?.[lang] || ""
          )}`}
        />

        <link
          rel="alternate"
          hrefLang="it"
          href={`${siteUrl}${normalizeUrl(
            pageData?.lblUrlPath?.it ||
              pageData?.lblUrlPath?.["it"] ||
              ""
          )}`}
        />
        <link
          rel="alternate"
          hrefLang="en"
          href={`${siteUrl}${normalizeUrl(
            pageData?.lblUrlPath?.en ||
              pageData?.lblUrlPath?.["en"] ||
              ""
          )}`}
        />

        {/* JSON-LD Structured Data - CON WebSite Schema (homepage) */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify([
              structuredData.webSiteSchema,
              structuredData.organizationSchema,
              structuredData.webPageSchema,
              ...(structuredData.breadcrumbSchema
                ? [structuredData.breadcrumbSchema]
                : []),
            ]),
          }}
        />
      </Head>

   
      <Script
        id="recaptcha"
        src={`https://www.google.com/recaptcha/api.js?render=${
          process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!
        }`}
        strategy="afterInteractive"
      />

 
      <Script
        id="sas-smart-js"
        src="https://ced.sascdn.com/tag/2661/smart.js"
        strategy="beforeInteractive"
      />

 
      <Script
        id="sas-init-home"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: sasInitScript }}
      />

      <main>
        <ModuleMatcher2 lang={lang} moduleData={dataPassToModuleMatcher} />

        <div id="sas_56461"></div>
        <Script type="application/javascript">
          {`
            var sas = window.sas = window.sas || {};
            sas.cmd = sas.cmd || [];
            sas.cmd.push(function() {
                sas.render("56461");
            });
          `}
        </Script>

        <div id="sas_56463"></div>
        <Script type="application/javascript">
          {`
            var sas = window.sas = window.sas || {};
            sas.cmd = sas.cmd || [];
            sas.cmd.push(function() {
                sas.render("56463");
            });
          `}
        </Script>
      </main>
    </>
  );
};

export default Homepage;
